import { useState, type ReactElement, type ReactNode } from "react";
import { Header } from "../components/Header";
import type { RoundButtonProps } from "../components/RoundButton";
import { Link } from "react-router-dom";
import { MEMBERSHIP_TIER_NAMES } from "@digu/shared";
import { Avatar, Cowrie, DcCoin, Icon } from "../components";
import { firstName, formatCompact, formatNumber } from "../lib/format";
import { useSession } from "../lib/session";
import { useNotifications } from "../lib/notifications";
import { NotificationsSheet } from "./Notifications";
import { YouMenu } from "./YouMenu";
import { Bunting } from "../components/Bunting";
import { liveOccasion, useOccasionsTick } from "../lib/occasions";

/** D-146: the greeting changes with the hour on this phone. */
export function greeting(hour: number): string {
  return hour < 5 ? "Good night," : hour < 12 ? "Good morning," : hour < 17 ? "Good afternoon," : hour < 21 ? "Good evening," : "Good night,";
}

/** D-180: while an occasion runs (and its looks are on), its greeting ("Happy Republic Day,"); otherwise by the hour. */
export function useGreeting(occasionLooks: boolean | undefined): string {
  useOccasionsTick();
  return liveOccasion(occasionLooks)?.home.greeting ?? greeting(new Date().getHours());
}

/**
 * The desktop top bar (rail layouts, App.tsx): a greeting by the hour and your first name (opens You) on the left; on the
 * right the same three things as the phone's app bar (D-171, D-196, D-200): tokens and Boli, opening the wallet, the bell,
 * and your picture, which opens the You menu. No plus here.
 */
export function TabHeader(): ReactElement {
  const { account, prefs } = useSession();
  const tier = account?.membership.active ? account.membership.tier : null;
  const hello = useGreeting(prefs?.occasionLooks);
  return (
    <div className="home-hd">
      <span className="home-hd__who">
        <span className="home-hd__greeting" data-testid="home-greeting">{hello}</span>
        <Link to="/you" className="home-hd__name-link hit44" aria-label={`You, ${account?.name ?? ""}${tier ? `, ${MEMBERSHIP_TIER_NAMES[tier]} member` : ""}`} data-testid="home-me">
          <span className="home-hd__name">{firstName(account?.name ?? "You")}</span>
          <Icon name="chevron-right" size={18} />
        </Link>
      </span>
      <span className="desk-top__right"><BalanceChip /><BellButton /><MeButton /></span>
    </div>
  );
}

/**
 * D-162: the bar at the top of every tab (Home, Tournaments, Clubs, Friends, Shop). D-205: the same bar on all five, with
 * no logo (the brand is on the app icon, the splash and the welcome pages, and the desktop rail): your picture (the You
 * menu) and your tokens and Boli (the Wallet) on the left, the bell on the right. The page's name is the heading under it.
 * The desktop rail shows the logo, balances and bell already, so there the bar is hidden and only the heading stays.
 */
export function AppBar(): ReactElement {
  const { prefs } = useSession();
  return (
    <>
    {/* D-180: the current occasion's bunting along the very top. */}
    <Bunting on={prefs?.occasionLooks} />
    <div className="appbar" data-testid="appbar">
      <MeButton />
      <BalanceChip />
      <span className="appbar__end"><BellButton /></span>
    </div>
    </>
  );
}

/**
 * D-196: the bell is back beside your tokens (D-163 had folded it into your picture). It carries the unread count and
 * opens What's new; on desktop a popover hangs from it.
 */
export function BellButton(): ReactElement {
  const { unread, markedRead } = useNotifications();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="hd-bell" onClick={() => setOpen(true)} aria-label={unread > 0 ? `What's new, ${formatNumber(unread)} new` : "What's new"} data-testid="home-bell">
        <Icon name="bell" size={22} />
        {unread > 0 && <span className="hd-bell__count" aria-hidden="true" data-testid="hd-count">{unread > 9 ? "9+" : unread}</span>}
      </button>
      <NotificationsSheet open={open} onClose={() => setOpen(false)} onRead={markedRead} />
    </>
  );
}

/** D-196: your picture opens the You menu (Admin first for admins, with what needs a look). No count on it any more. */
export function MeButton(): ReactElement {
  const { account } = useSession();
  const [open, setOpen] = useState(false);
  const name = account?.name ?? "You";
  const tier = account?.membership.active ? account.membership.tier : null;
  return (
    <>
      <button type="button" className="hd-me" onClick={() => setOpen(true)} aria-label={`${name}${tier ? `, ${MEMBERSHIP_TIER_NAMES[tier]} member` : ""}. Your profile, wallet and settings`} aria-haspopup="dialog" data-testid="hd-me">
        <Avatar publicId={account?.publicId ?? null} photoVersion={account?.photoVersion ?? 0} name={name} size={40} />
        {tier && <span className="home-hd__badge" aria-hidden="true" data-testid="hd-gem"><Icon name="gem" size={12} /></span>}
      </button>
      <YouMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** A tab page's top: the bar, then the page's name (and its own actions, on desktop only). */
export function TabPageHeader({ title, actions = [], subtitle }: { title: string; actions?: RoundButtonProps[]; subtitle?: ReactNode }): ReactElement {
  return <><AppBar /><Header title={title} actions={actions} subtitle={subtitle} className="header--tab" /></>;
}

/**
 * D-200 (owner): tokens and Boli, small, opening the wallet (which shows the exact amounts). D-171 had tokens only.
 */
export function BalanceChip(): ReactElement | null {
  const { wallet } = useSession();
  if (!wallet) return null;
  return (
    <Link to="/wallet" className="hd-balance" aria-label={`${formatNumber(wallet.tokens)} tokens, ${formatNumber(wallet.boli)} Boli. Open the wallet.`} data-testid="hd-balance">
      <span className={`hd-balance__row${wallet.tokens === 0 ? " hd-balance__row--empty" : ""}`}><DcCoin size={20} />{formatCompact(wallet.tokens)}</span>
      <span className="hd-balance__row"><Cowrie size={20} />{formatCompact(wallet.boli)}</span>
    </Link>
  );
}

